import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Shield, Check, Star, Phone } from 'lucide-react';
import { products } from '../data/products';
import { WhatsAppService } from '../utils/whatsapp';
import { OrderFormData } from '../types/OrderForm';

const ProductPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState<OrderFormData>({
    name: '',
    phone: '',
    address: '',
    carModel: '',
    quantity: 1,
    selectedProducts: [], // Initialize with empty array
    city: '', // Add city field
    notes: '' // Add notes field
  });

  const product = products.find(p => p.id === productId);

  // Update selectedProducts when product changes
  useEffect(() => {
    if (product) {
      setFormData(prev => ({
        ...prev,
        selectedProducts: [product]
      }));
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;
    
    // Facebook Pixel - ViewContent
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_ids: [product.id],
        content_name: product.name,
        content_type: 'product',
        value: product.price,
        currency: 'TND'
      });
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">المنتج غير موجود</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            العودة إلى الصفحة الرئيسية
          </Link>
        </div>
      </div>
    );
  }

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Facebook Pixel - Purchase
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Purchase', {
        content_ids: [product.id],
        content_name: product.name,
        content_type: 'product',
        value: product.price * quantity,
        currency: 'TND'
      });
    }

    // Prepare form data with product info
    const orderData = {
      ...formData,
      quantity: quantity,
      selectedProducts: [product]
    };

    const totalPrice = product.price * quantity;

    try {
      // Send order via CallMeBot
      const success = await WhatsAppService.sendOrderMessage(orderData, totalPrice);
      
      if (success) {
        setSubmitSuccess(true);
        setShowOrderForm(false);
        // Reset form after successful submission
        setFormData({
          name: '',
          phone: '',
          address: '',
          carModel: '',
          quantity: 1,
          selectedProducts: [],
          city: '',
          notes: ''
        });
        setQuantity(1);
      } else {
        alert('حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.');
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrice = product.price * quantity;
  const savings = product.originalPrice ? (product.originalPrice - product.price) * quantity : 0;

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 space-x-reverse text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-5 h-5" />
              <span>العودة</span>
            </Link>
            <a href="tel:+21612345678" className="flex items-center space-x-2 space-x-reverse text-gray-600">
              <Phone className="w-5 h-5" />
              <span className="hidden sm:inline">+216 12 345 678</span>
            </a>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Product Images */}
          <div className="space-y-4">
            <div className="relative aspect-square bg-white rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={product.images[currentImageIndex]} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.badge && (
                <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {product.badge}
                </div>
              )}
              {savings > 0 && (
                <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  وفر {savings.toFixed(0)} د.ت
                </div>
              )}
            </div>
            
            {product.images.length > 1 && (
              <div className="flex space-x-2 space-x-reverse">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      currentImageIndex === index ? 'border-blue-500' : 'border-gray-200'
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{product.name}</h1>
              <p className="text-gray-600 text-lg leading-relaxed">{product.description}</p>
            </div>

            {/* Price */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center space-x-4 space-x-reverse mb-4">
                <span className="text-4xl font-bold text-blue-600">{product.price} د.ت</span>
                {product.originalPrice && (
                  <span className="text-2xl text-gray-400 line-through">{product.originalPrice} د.ت</span>
                )}
              </div>
              
              {/* Quantity */}
              <div className="flex items-center space-x-4 space-x-reverse mb-6">
                <label className="text-gray-700 font-medium">الكمية:</label>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-300"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-300"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex justify-between">
                  <span>المجموع الفرعي:</span>
                  <span className="font-semibold">{totalPrice} د.ت</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>التوصيل:</span>
                  <span className="font-semibold">مجاني</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>التوفير:</span>
                    <span className="font-semibold">-{Math.floor(savings)} د.ت</span>
                  </div>
                )}
                <hr />
                <div className="flex justify-between text-xl font-bold">
                  <span>المجموع:</span>
                  <span>{totalPrice} د.ت</span>
                </div>
              </div>

              <button
                onClick={() => setShowOrderForm(true)}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 space-x-reverse transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                <span>اطلب الان</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Order Form Modal */}
      {showOrderForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-right">إتمام الطلب</h3>
            
            <form onSubmit={handleOrderSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-right">الاسم الكامل *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                  placeholder="الاسم الكامل"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-right">رقم الهاتف *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                  placeholder="+216 XX XXX XXX"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-right">المدينة *</label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                  placeholder="مدينتك"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2 text-right">العنوان الكامل *</label>
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                  placeholder="عنوان التوصيل"
                />
              </div>
              


              <div className="flex space-x-3 space-x-reverse pt-4">
                <button
                  type="button"
                  onClick={() => setShowOrderForm(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'جاري الإرسال...' : 'اطلب الآن'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Message Modal */}
      {submitSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">تم إرسال طلبك بنجاح!</h3>
            <p className="text-gray-600 mb-6">سيتم التواصل معك قريباً لتأكيد الطلب وتفاصيل التوصيل.</p>
            <button
              onClick={() => setSubmitSuccess(false)}
              className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold"
            >
              حسناً
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;